import type { GetRowIdParams } from '@ag-grid-community/core'
import { useCallback, useMemo, useState } from 'react'
import {
  AncillaryRelationship,
  AncillaryReportRow,
} from '../api/ancillary-report.types'

export type FamilyKey = string & { readonly __brand: 'FamilyKey' }

export type FoldedDependent = {
  id: string
  name: string
  relationship: AncillaryRelationship
  employmentStatus: string | null
  problem: boolean
}

export type FamilyPlacement =
  | { recordType: 'single'; key: FamilyKey | null }
  | {
      recordType: 'group'
      key: FamilyKey
      expanded: false
      others: number
      folded: readonly [FoldedDependent, ...FoldedDependent[]]
    }
  | {
      recordType: 'group'
      key: FamilyKey
      expanded: true
      others: number
      folded: readonly []
    }

export type FamilyRow = AncillaryReportRow & {
  rowId: string
  family: FamilyPlacement
}

const RELATIONSHIP_RANK: Record<AncillaryRelationship, number> = {
  employee: 0,
  spouse: 1,
  child: 2,
}

type Identified = AncillaryReportRow & { rowId: string }

function mintFamilyKey(employeePublicKey: string): FamilyKey {
  return employeePublicKey as FamilyKey
}

function withRowIds(rows: readonly AncillaryReportRow[]): Identified[] {
  const seen = new Map<string, number>()
  return rows.map((row) => {
    if (row.personPublicKey != null) {
      return {
        ...row,
        rowId: `${row.employeePublicKey}|${row.personPublicKey}`,
      }
    }
    const base = `${row.employeePublicKey}|noyo:${row.relationship}:${row.firstName} ${row.lastName}`
    const n = seen.get(base) ?? 0
    seen.set(base, n + 1)
    return { ...row, rowId: `${base}:${n}` }
  })
}

function comparePeople(
  a: Pick<Identified, 'relationship' | 'lastName' | 'firstName' | 'rowId'>,
  b: Pick<Identified, 'relationship' | 'lastName' | 'firstName' | 'rowId'>
): number {
  const byRelationship =
    RELATIONSHIP_RANK[a.relationship] - RELATIONSHIP_RANK[b.relationship]
  if (byRelationship !== 0) return byRelationship
  const byName = `${a.lastName} ${a.firstName}`.localeCompare(
    `${b.lastName} ${b.firstName}`
  )
  if (byName !== 0) return byName
  return a.rowId.localeCompare(b.rowId)
}

function personHasProblem(row: AncillaryReportRow): boolean {
  if (row.dental?.problem) return true
  if (row.vision?.problem) return true
  return Object.values(row.otherLines).some((cell) => cell?.problem)
}

function foldDependent(person: Identified): FoldedDependent {
  return {
    id: person.rowId,
    name: `${person.firstName} ${person.lastName}`.trim(),
    relationship: person.relationship,
    employmentStatus: person.employmentStatus,
    problem: personHasProblem(person),
  }
}

function isSolo(people: Identified[]): people is [Identified] {
  return people.length === 1
}

function isMulti(
  people: Identified[]
): people is [Identified, Identified, ...Identified[]] {
  return people.length > 1
}

function foldedDependents(
  members: readonly [Identified, ...Identified[]]
): readonly [FoldedDependent, ...FoldedDependent[]] {
  const [first, ...rest] = members
  return [foldDependent(first), ...rest.map(foldDependent)]
}

function placeFamilies(
  rows: readonly AncillaryReportRow[],
  open: ReadonlySet<FamilyKey>
): FamilyRow[] {
  const buckets = new Map<string, Identified[]>()
  const order: string[] = []
  for (const person of withRowIds(rows)) {
    const bucket = buckets.get(person.employeePublicKey)
    if (bucket) {
      bucket.push(person)
    } else {
      buckets.set(person.employeePublicKey, [person])
      order.push(person.employeePublicKey)
    }
  }

  const visible: FamilyRow[] = []
  for (const employeePublicKey of order) {
    const bucket = buckets.get(employeePublicKey)
    if (!bucket) continue
    const sorted = [...bucket].sort(comparePeople)
    if (isSolo(sorted)) {
      visible.push({
        ...sorted[0],
        family: { recordType: 'single', key: null },
      })
      continue
    }
    if (!isMulti(sorted)) continue
    const key = mintFamilyKey(employeePublicKey)
    const [lead, ...members] = sorted
    const others = members.length
    const expanded = open.has(key)
    if (expanded) {
      visible.push({
        ...lead,
        family: {
          recordType: 'group',
          key,
          expanded: true,
          others,
          folded: [],
        },
      })
      for (const member of members) {
        visible.push({ ...member, family: { recordType: 'single', key } })
      }
      continue
    }
    visible.push({
      ...lead,
      family: {
        recordType: 'group',
        key,
        expanded: false,
        others,
        folded: foldedDependents(members),
      },
    })
  }
  return visible
}

// A column sort orders every visible row. Members still have to follow their lead, in relationship order.
function postSortRows(params: { nodes: { data?: FamilyRow }[] }): void {
  const membersByKey = new Map<FamilyKey, { data?: FamilyRow }[]>()
  const heads: { data?: FamilyRow }[] = []
  for (const node of params.nodes) {
    const family = node.data?.family
    if (family?.recordType === 'single' && family.key != null) {
      const group = membersByKey.get(family.key)
      if (group) group.push(node)
      else membersByKey.set(family.key, [node])
    } else {
      heads.push(node)
    }
  }
  for (const group of Array.from(membersByKey.values())) {
    group.sort((left, right) => {
      if (!left.data || !right.data) return 0
      return comparePeople(left.data, right.data)
    })
  }
  const ordered: { data?: FamilyRow }[] = []
  for (const node of heads) {
    ordered.push(node)
    const family = node.data?.family
    if (family?.recordType !== 'group') continue
    const group = membersByKey.get(family.key)
    if (group) ordered.push(...group)
  }
  params.nodes.splice(0, params.nodes.length, ...ordered)
}

function getRowId(params: GetRowIdParams<FamilyRow>): string {
  return params.data.rowId
}

export function useAncillaryFamilies(rows: readonly AncillaryReportRow[]) {
  const [openKeys, setOpenKeys] = useState<ReadonlySet<FamilyKey>>(
    () => new Set()
  )
  const setExpanded = useCallback((key: FamilyKey, open: boolean) => {
    setOpenKeys((current) => {
      if (current.has(key) === open) return current
      const next = new Set(current)
      if (open) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])
  const rowData = useMemo(() => placeFamilies(rows, openKeys), [rows, openKeys])
  const grid = useMemo(
    () => ({
      rowData,
      getRowId,
      postSortRows,
    }),
    [rowData]
  )
  return { grid, setExpanded }
}
